import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface ConversationListItem {
  conversation_id: string;
  title: string;
  message_count: number;
  last_message_at: string;
  created_at: string;
}

interface ChatHistoryState {
  // State
  conversations: ConversationListItem[];
  activeConversationId: string | null;
  isLoadingHistory: boolean;
  refreshTrigger: number; // Timestamp to trigger refreshes
  pendingQuestion: string | null; // Question from floating widget to process on chat page
  
  // Actions
  setConversations: (conversations: ConversationListItem[]) => void;
  setActiveConversation: (conversationId: string | null) => void;
  addConversation: (conversation: ConversationListItem) => void;
  removeConversation: (conversationId: string) => void;
  updateConversation: (conversationId: string, updates: Partial<ConversationListItem>) => void;
  clearHistory: () => void;
  setLoadingHistory: (loading: boolean) => void;
  triggerRefresh: () => void; // Trigger a refresh in listening components
  setPendingQuestion: (question: string | null) => void;
  clearPendingQuestion: () => void;
  resetSession: () => void;
}

export const useChatHistoryStore = create<ChatHistoryState>()(
  persist(
    (set, get) => ({
      // Initial state
      conversations: [],
      activeConversationId: null,
      isLoadingHistory: false,
      refreshTrigger: 0,
      pendingQuestion: null,
      
      // Actions
      setConversations: (conversations) => 
        set({ conversations }),
      
      setActiveConversation: (conversationId) => 
        set({ activeConversationId: conversationId }),
      
      triggerRefresh: () =>
        set({ refreshTrigger: Date.now() }),
      
      setPendingQuestion: (question) =>
        set({ pendingQuestion: question }),
      
      clearPendingQuestion: () =>
        set({ pendingQuestion: null }),

      resetSession: () =>
        set({
          conversations: [],
          activeConversationId: null,
          isLoadingHistory: false,
          refreshTrigger: 0,
          pendingQuestion: null,
        }),
      
      addConversation: (conversation) =>
        set((state) => {
          // Check if conversation already exists
          const exists = state.conversations.some(
            c => c.conversation_id === conversation.conversation_id
          );
          
          if (exists) {
            // Update existing
            return {
              conversations: state.conversations.map(c =>
                c.conversation_id === conversation.conversation_id
                  ? conversation
                  : c
              )
            };
          }
          
          // Add new conversation at the beginning
          return {
            conversations: [conversation, ...state.conversations]
          };
        }),
      
      removeConversation: (conversationId) =>
        set((state) => ({
          conversations: state.conversations.filter(
            (c) => c.conversation_id !== conversationId
          ),
          activeConversationId: 
            state.activeConversationId === conversationId 
              ? null 
              : state.activeConversationId
        })),
      
      updateConversation: (conversationId, updates) =>
        set((state) => ({
          conversations: state.conversations.map((c) =>
            c.conversation_id === conversationId
              ? { ...c, ...updates }
              : c
          )
        })),
      
      clearHistory: () =>
        set({ conversations: [], activeConversationId: null }),
      
      setLoadingHistory: (loading) =>
        set({ isLoadingHistory: loading }),
    }),
    {
      name: 'chat-history-storage',
      partialize: (state) => ({
        conversations: state.conversations,
        activeConversationId: state.activeConversationId,
      }),
    }
  )
);
